import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Feature, Geometry, GeoJsonProperties } from 'geojson';

export interface AracProperties {
  'ARAC_Lot No.': string | null;            // For filtering
  'ARAC_Zone ': string | null;              // Note the space at the end
  'ARAC_Description Type ': string | null;   // Note the space at the end
  'ARAC_Lot Id (Database) ': string | null;  // Note the space at the end
  'ARAC_Sublease Holder ': string | null;    // Note the space at the end
  'ARAC_Sublease Expiry ': string | null;    // Note the space at the end
  'ARAC_Survey Plan # ': string | null;      // Note the space at the end
  LAISKEY?: string;
  lot_avail?: string;                       // Added availability info
  [key: string]: any;
}

export type MapFeature = Feature<Geometry, AracProperties>;

// Interface for community outline features with name property
export interface CommunityOutlineFeature extends Feature<Geometry> {
  properties: GeoJsonProperties & {
    communityName?: string;
  };
}

interface LotData {
  lot_id: string;
  lot_avail: string;
}

export const useMapData = () => {
  const [features, setFeatures] = useState<MapFeature[]>([]);
  const [communityOutlineFeatures, setCommunityOutlineFeatures] = useState<CommunityOutlineFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Helper function to extract community name from filename
        const extractCommunityName = (filename: string): string => {
          // Remove "Outline.geojson" and format the name
          const name = filename.replace('Outline.geojson', '');
          
          // Format special cases
          if (name === 'BARTALUMBABAY') return 'BARTALUMBA BAY';
          if (name === 'FOURMILE') return 'FOUR MILE';
          if (name === 'LITTLEPARADISE') return 'LITTLE PARADISE';
          
          // Add spaces to names that should have them
          return name.replace(/([A-Z])/g, ' $1').trim();
        };

        // Function to load a community outline and add the community name
        const loadCommunityOutline = async (filename: string): Promise<CommunityOutlineFeature[]> => {
          const response = await fetch(`/data/${filename}`);
          if (!response.ok) {
            throw new Error(`Failed to load ${filename} GeoJSON data`);
          }
          const data = await response.json();
          
          // Extract community name from filename
          const communityName = extractCommunityName(filename);
          
          // Add community name to each feature
          return data.features.map((feature: Feature<Geometry>) => ({
            ...feature,
            properties: {
              ...feature.properties,
              communityName
            }
          }));
        };
        
        // List of community outline files
        const communityFiles = [
          'MILYAKBURRAOutline.geojson',
          'UMBAKUMBAOutline.geojson',
          'BARTALUMBABAYOutline.geojson',
          'MALKALAOutline.geojson',
          'LITTLEPARADISEOutline.geojson',
          'ANGURUGUOutline.geojson',
          'FOURMILEOutline.geojson'
        ];
        
        // Load all community outlines
        const communityOutlinesPromises = communityFiles.map(loadCommunityOutline);
        const communityOutlinesArrays = await Promise.all(communityOutlinesPromises);
        
        // Flatten the arrays of features
        const allCommunityOutlines = communityOutlinesArrays.flat();
        
        setCommunityOutlineFeatures(allCommunityOutlines);

        // Fetch Cadastre GeoJSON data
        const geojsonResponse = await fetch('/data/CadastrewithARAC02.geojson');
        if (!geojsonResponse.ok) {
          throw new Error('Failed to load Cadastre GeoJSON data');
        }
        const geojsonData = await geojsonResponse.json();

        // Filter features with ARAC data
        const aracFeatures = geojsonData.features.filter(
          (feature: MapFeature) => feature.properties['ARAC_Lot No.'] !== null
        );

        // Fetch lot availability data from Supabase
        console.log('Fetching lot availability data from land_lots table');
        const { data: lotData, error: lotError } = await supabase
          .from('land_lots')
          .select('lot_id, lot_avail');
        
        if (lotError) {
          console.error('Error fetching lot availability data:', lotError);
          throw lotError;
        }

        // Analyze the distribution of availability values
        const availableLots = lotData.filter(lot => lot.lot_avail === 'Y').length;
        const unavailableLots = lotData.filter(lot => lot.lot_avail === 'N').length;
        const otherValues = lotData.filter(lot => lot.lot_avail !== 'Y' && lot.lot_avail !== 'N').length;
        
        console.log('Availability distribution:', { 
          availableLots, 
          unavailableLots,
          otherValues,
          total: lotData.length,
          uniqueValues: [...new Set(lotData.map(lot => lot.lot_avail))],
          sampleValues: lotData.slice(0, 10).map(lot => ({id: lot.lot_id, avail: lot.lot_avail}))
        });
        
        console.log('Examining first few lot IDs from land_lots:', {
          lotIds: lotData.slice(0, 10).map(lot => lot.lot_id)
        });

        // Map of lot_id to availability status - USING DIRECT VALUES, NO FLIPPING
        // Create a reverse lookup object that maps from GeoJSON ID to lot_id for easier debugging
        const reverseIdLookup: Record<string, string> = {};
        
        // Compare GeoJSON IDs with lot_ids to see if there's a pattern we can use for matching
        aracFeatures.slice(0, 20).forEach((feature: MapFeature) => {
          const geoJsonId = feature.properties['ARAC_Lot Id (Database) ']?.trim();
          if (geoJsonId) {
            // Try to find a matching lot_id
            const matchingLot = lotData.find(lot => 
              lot.lot_id.trim() === geoJsonId || 
              lot.lot_id.trim().toLowerCase() === geoJsonId.toLowerCase()
            );
            if (matchingLot) {
              reverseIdLookup[geoJsonId] = matchingLot.lot_id;
            }
          }
        });
        
        console.log('Reverse ID lookup (GeoJSON ID -> lot_id):', {
          count: Object.keys(reverseIdLookup).length,
          sample: Object.entries(reverseIdLookup).slice(0, 5)
        });
        
        // Create availability map with case-insensitive keys for more robust matching
        const availabilityMap = lotData.reduce((map: Record<string, string>, lot: LotData) => {
          if (!lot.lot_id) return map;
          
          // Normalize lot_id by trimming any whitespace to ensure reliable matching
          const normalizedLotId = lot.lot_id.trim();
          
          // Add both original and lowercase versions of the ID for more robust matching
          map[normalizedLotId] = lot.lot_avail;
          map[normalizedLotId.toLowerCase()] = lot.lot_avail;
          
          // Also add versions without the "Angur-" prefix for matching with lot numbers
          if (normalizedLotId.startsWith('Angur-')) {
            const numberOnly = normalizedLotId.substring(6); // Remove "Angur-"
            map[numberOnly] = lot.lot_avail;
          }
          
          return map;
        }, {});

        console.log('Availability map created with direct values:', { 
          mapSize: Object.keys(availabilityMap).length,
          sampleEntries: Object.entries(availabilityMap).slice(0, 5)
        });

        // Examine first few GeoJSON features to debug the joining issue
        console.log('Examining first few GeoJSON features:', {
          featureIds: aracFeatures.slice(0, 5).map((f: MapFeature) => {
            const rawId = f.properties['ARAC_Lot Id (Database) '];
            return {
              rawId,
              trimmedId: rawId?.trim(),
              hasTrailingSpace: rawId !== rawId?.trim()
            };
          })
        });

        // Enrich features with availability data
        const enrichedFeatures = aracFeatures.map((feature: MapFeature) => {
          // Get the lot ID, being extra careful with trailing spaces
          // The GeoJSON file has 'ARAC_Lot Id (Database) ' with a trailing space
          const lotIdWithTrailingSpace = feature.properties['ARAC_Lot Id (Database) '];
          // Trim it to match the database ID format which doesn't have the trailing space
          const lotId = lotIdWithTrailingSpace?.trim();
          
          // Log to debug the mapping process
          console.log('Mapping lot:', { 
            rawLotId: lotIdWithTrailingSpace,
            trimmedLotId: lotId,
            hasAvailData: lotId && availabilityMap[lotId] !== undefined ? 'Yes' : 'No',
            availValue: lotId && availabilityMap[lotId] !== undefined ? availabilityMap[lotId] : 'undefined',
            availMapKeys: Object.keys(availabilityMap).slice(0, 5)
          });
          
          // CRITICAL CHANGE: Try multiple ways to find the availability data
          if (lotId) {
            // First try direct match
            if (availabilityMap[lotId] !== undefined) {
              console.log(`Found direct match for lot ${lotId}: ${availabilityMap[lotId]}`);
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  lot_avail: availabilityMap[lotId]
                }
              };
            }
            // Then try lowercase match
            else if (typeof lotId === 'string' && availabilityMap[lotId.toLowerCase()] !== undefined) {
              console.log(`Found lowercase match for lot ${lotId}: ${availabilityMap[lotId.toLowerCase()]}`);
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  lot_avail: availabilityMap[lotId.toLowerCase()]
                }
              };
            }
            // If we have a reverse lookup, try that
            else if (reverseIdLookup[lotId] && availabilityMap[reverseIdLookup[lotId]] !== undefined) {
              console.log(`Found match via reverse lookup for lot ${lotId}: ${availabilityMap[reverseIdLookup[lotId]]}`);
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  lot_avail: availabilityMap[reverseIdLookup[lotId]]
                }
              };
            }
            
            // Try using just the lot number from the ARAC_Lot No. field
            const lotNo = feature.properties['ARAC_Lot No.']?.trim();
            if (lotNo && availabilityMap[lotNo] !== undefined) {
              console.log(`Found match using lot number ${lotNo}: ${availabilityMap[lotNo]}`);
              return {
                ...feature,
                properties: {
                  ...feature.properties,
                  lot_avail: availabilityMap[lotNo]
                }
              };
            }
          }
          
          return feature;
        });

        // Log first feature's properties to verify field names
        if (enrichedFeatures.length > 0) {
          console.log('First feature properties with availability:', {
            properties: enrichedFeatures[0].properties,
            propertyNames: Object.keys(enrichedFeatures[0].properties),
            availability: enrichedFeatures[0].properties.lot_avail
          });
        }

        setFeatures(enrichedFeatures);
      } catch (err) {
        console.error('Error loading map data:', err);
        setError(err instanceof Error ? err.message : 'Error loading map data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return {
    features,
    communityOutlineFeatures,
    loading,
    error
  };
};
